// src/team/PaymentForm.tsx: the payment form (Settings, Activate and the quote page; docs/SPEC.md 16.6; docs/DECISIONS.md
// "Billing" and "Paying by bank: one button (owner, 2026-10-27)"), behind a SetupIntent the server makes for this form.
//
// Two choices, ours, bank account first. A bank account is one button, Connect Your Bank, that opens Stripe's bank window
// (search, the bank's own sign-in, or the numbers by hand with small deposits); once one is in, it is one line with Change and
// the debit authorization Stripe requires us to show. Stripe's Payment Element drew a grid of twelve bank tiles here, which
// the owner asked about ("do we have to display the banks like this?"); it has no setting to leave them out. A card is
// Stripe's Payment Element with the card alone (deferred: the SetupIntent's secret goes with the confirmation), styled as our
// own fields (src/lib/stripe.ts#appearance).
//
// The billing address (sales tax needs it) is one line with Edit when it is known already (the one saved with the last
// method, else the builder's office): Edit opens Stripe's Address Element, filled in with it. When none is known the
// Address Element shows at once. It is asked for when the parent's button is pressed, never before: Connect Your Bank opens
// the bank window whatever the address holds (owner, 2026-10-27: it "throws an error when someone doesn't add their
// address.. which is after the button"). A card sends it with the confirmation; either way confirm() answers it and the
// server makes it the payment method's (billing/service.ts applySetup), which is how a bank account gets it.
//
// The parent owns the button: it gets a handle whose confirm() checks the fields, confirms the SetupIntent and answers
// its id and the address, which the parent hands to POST /api/team/billing/setup/complete (or the quote's accept). Stripe.js
// loads only when a form opens.
// Root class: bill-form.
import { useEffect, useRef, useState } from "preact/hooks";
import type { BillingAddress, BillingSetupPurpose, BillingSetupResponse } from "../../shared/api";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import {
  appearance, fonts, loadStripe, type StripeAddress, type StripeBankSetupIntent, type StripeElement, type StripeElements, type StripeInstance,
} from "../lib/stripe";
import { me, tenant } from "../state/session";
import { Button, cx, Icon, LinkButton, Skeleton } from "../ui";

export type ConfirmResult = { ok: true; setupIntentId: string; address: BillingAddress } | { ok: false; message: string };
/** `mark` hears "submit" and "confirm" as each finishes, so a slow run can be read (BillingStep logs them). */
export type PaymentFormHandle = { confirm(mark?: (step: "submit" | "confirm") => void): Promise<ConfirmResult> };

/** Where Stripe sends the browser back if a bank or card check ever needs a full-page step (BillingSection reads it). */
export const RETURN_PATH = "/team/settings?billing=return";

const PHONE_FIELDS = "(max-width: 1023px), (pointer: coarse)";
const TRY_AGAIN = "That didn't go through. Check the details and try again.";

/**
 * The debit authorization for a bank account kept on file (Stripe's recommended online mandate, both parts: Nacha requires
 * it shown before the SetupIntent is confirmed). The parent's button is the "continue".
 */
export const BANK_MANDATE =
  "By continuing, you authorize homebuild to debit the bank account above for any amount owed for charges arising from your use of homebuild's services, pursuant to homebuild's website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to homebuild with 30 (thirty) days notice. If you use homebuild's services periodically pursuant to homebuild's terms, you authorize homebuild to debit your bank account periodically. Payments that fall outside of the regular debits authorized above will only be debited after your authorization is obtained.";

type Kind = "us_bank_account" | "card";
const KINDS: readonly { id: Kind; label: string; icon: "bank" | "card" }[] = [
  { id: "us_bank_account", label: "Bank Account", icon: "bank" },
  { id: "card", label: "Card", icon: "card" },
];
type Bank = { name: string; last4: string };

function toAddress(a: StripeAddress): BillingAddress {
  return { line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state, postal_code: a.postal_code, country: a.country };
}

function toStripe(a: BillingAddress): StripeAddress {
  return { line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state, postal_code: a.postal_code, country: a.country };
}

/** "12 Main St, Suite A, Springfield, NY 12345". */
export function addressLine(a: BillingAddress): string {
  return [a.line1, a.line2, a.city, [a.state, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

/** The bank Stripe's window answered with, as one line's parts. */
function bankOf(si: StripeBankSetupIntent | undefined): { name: string; last4: string } {
  const pm = si?.payment_method;
  const b = pm && typeof pm === "object" ? pm.us_bank_account : null;
  return { name: b?.bank_name || "Bank account", last4: b?.last4 || "" };
}

type Phase = "starting" | "ready" | "failed";

export function PaymentForm(props: {
  purpose: BillingSetupPurpose;
  onHandle: (h: PaymentFormHandle | null) => void;
  /** The address to start from (one line with Edit); null: the address fields show at once. */
  address?: BillingAddress | null;
  /** The billing address while it is being edited, once it is complete (null while it isn't): today's sales tax. */
  onAddress?: (a: BillingAddress | null) => void;
  /** Whether the payment method is in (a bank connected, or the card fields complete), for a button that waits for it. */
  onComplete?: (complete: boolean) => void;
  /** Which kind of payment is chosen: "card" or "us_bank_account" (the button names its stages with it). */
  onMethod?: (type: string | null) => void;
  /**
   * The quote page (src/quote): its own route for the form's SetupIntent instead of the team's, the company the address is
   * for (there is no signed-in builder), and the person, whose name and email the page asks above the payment fields: Stripe's
   * own name and email fields are left out and the person's go with the confirmation.
   */
  open?: () => Promise<BillingSetupResponse>;
  company?: string;
  contact?: () => { name: string; email: string };
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const addrRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const cardEl = useRef<StripeElement | null>(null);
  const stripeRef = useRef<StripeInstance | null>(null);
  const secretRef = useRef("");
  const [phase, setPhase] = useState<Phase>("starting");
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [kind, setKind] = useState<Kind>("us_bank_account");
  const kindRef = useRef(kind);
  kindRef.current = kind;
  const [bank, setBank] = useState<Bank | null>(null);
  const bankRef = useRef(bank);
  bankRef.current = bank;
  const [connecting, setConnecting] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [cardShown, setCardShown] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const known = props.address ?? null;
  const [editing, setEditing] = useState(!known);
  // The address that goes with the payment method: the known one, or what the address fields hold.
  const current = useRef<StripeAddress | null>(known ? toStripe(known) : null);
  const onHandle = useRef(props.onHandle);
  onHandle.current = props.onHandle;
  const onAddress = useRef(props.onAddress);
  onAddress.current = props.onAddress;
  const onComplete = useRef(props.onComplete);
  onComplete.current = props.onComplete;
  const onMethod = useRef(props.onMethod);
  onMethod.current = props.onMethod;
  const contact = useRef(props.contact);
  contact.current = props.contact;

  useEffect(() => {
    let alive = true;
    setPhase("starting");
    setFailure(null);
    setBank(null);
    setCardShown(false);
    setCardReady(false);
    setCardComplete(false);
    const fail = (message: string | null) => {
      if (!alive) return;
      setFailure(message);
      setPhase("failed");
      onHandle.current(null);
    };
    void (async () => {
      try {
        const form = props.open ? await props.open() : props.purpose === "start" ? await api.billingSetup() : await api.billingPaymentMethod();
        const stripe = await loadStripe(form.publishableKey);
        if (!alive) return;
        const phone = typeof window.matchMedia === "function" && window.matchMedia(PHONE_FIELDS).matches;
        // Deferred: the card fields alone (no tabs, no bank grid); the SetupIntent's secret goes with the confirmation.
        const elements = stripe.elements({ mode: "setup", currency: "usd", paymentMethodTypes: ["card"], appearance: appearance(phone), fonts: fonts(), loader: "auto" });
        stripeRef.current = stripe;
        secretRef.current = form.clientSecret;
        elementsRef.current = elements;
        setPhase("ready");
        onHandle.current({
          confirm: async (mark) => {
            const address = current.current;
            if (kindRef.current === "card") {
              const checked = await elements.submit();
              mark?.("submit");
              if (checked.error) return { ok: false, message: checked.error.message ?? TRY_AGAIN };
              if (!address) return { ok: false, message: "Add the billing address." };
              const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret: form.clientSecret,
                redirect: "if_required",
                confirmParams: { return_url: `${location.origin}${RETURN_PATH}`, payment_method_data: { billing_details: { address, ...(contact.current ? contact.current() : {}) } } },
              });
              mark?.("confirm");
              if (error || !setupIntent) {
                // The fields show their own errors; a decline says what happened in Stripe's plain words.
                const said = error && (error.type === "card_error" || error.type === "validation_error") ? error.message : null;
                return { ok: false, message: said ?? TRY_AGAIN };
              }
              return { ok: true, setupIntentId: setupIntent.id, address: toAddress(address) };
            }
            mark?.("submit");
            if (!bankRef.current) return { ok: false, message: "Connect your bank first." };
            if (!address) return { ok: false, message: "Add the billing address." };
            const { error, setupIntent } = await stripe.confirmUsBankAccountSetup(form.clientSecret);
            mark?.("confirm");
            if (error || !setupIntent || setupIntent.status === "requires_payment_method") return { ok: false, message: error?.message ?? TRY_AGAIN };
            // Succeeded, or waiting on small deposits: the server reads which, and gives the account this address.
            return { ok: true, setupIntentId: setupIntent.id, address: toAddress(address) };
          },
        });
      } catch (e) {
        fail(e instanceof ApiError ? e.message : null);
      }
    })();
    return () => {
      alive = false;
      cardEl.current?.destroy();
      cardEl.current = null;
      elementsRef.current = null;
      stripeRef.current = null;
      onHandle.current(null);
      onComplete.current?.(false);
    };
  }, [props.purpose, attempt]);

  // The card fields, mounted the first time Card is chosen (never into a hidden box) and kept after.
  useEffect(() => {
    const elements = elementsRef.current;
    if (phase !== "ready" || kind !== "card" || cardShown || !elements || !cardRef.current) return;
    const name = props.company ?? tenant.value?.name ?? "";
    const email = props.contact ? "" : (me.value?.user?.email ?? "");
    const payment: StripeElement = elements.create("payment", {
      layout: { type: "tabs" },
      // No Apple Pay or Google Pay (the Permissions-Policy keeps payment off), and no Link: with it Stripe adds its own
      // "Card | Bank" tabs and a sign-up for Link above the card fields. The address is the form's, sent on confirm.
      wallets: { applePay: "never", googlePay: "never", link: "never" },
      fields: { billingDetails: props.contact ? { address: "never", name: "never", email: "never" } : { address: "never" } },
      ...(props.contact ? {} : { defaultValues: { billingDetails: { name, email } } }),
    });
    payment.on("ready", () => setCardReady(true));
    payment.on("loaderror", () => {
      setFailure(null);
      setPhase("failed");
      onHandle.current(null);
    });
    payment.on("change", (e) => setCardComplete(!!e.complete));
    payment.mount(cardRef.current);
    cardEl.current = payment;
    setCardShown(true);
  }, [phase, kind]);

  // What the parent's button reads: the kind chosen, and whether its payment method is in.
  useEffect(() => {
    onMethod.current?.(phase === "ready" ? kind : null);
    onComplete.current?.(phase === "ready" && (kind === "card" ? cardComplete : !!bank));
  }, [phase, kind, cardComplete, bank]);

  const connect = async () => {
    const stripe = stripeRef.current;
    if (!stripe || connecting) return;
    const who = contact.current ? contact.current() : { name: props.company ?? tenant.value?.name ?? "", email: me.value?.user?.email ?? "" };
    if (!who.name.trim()) {
      setBankError("Add your name first, then connect your bank.");
      return;
    }
    setBankError(null);
    setConnecting(true);
    try {
      const { error, setupIntent } = await stripe.collectBankAccountForSetup({
        clientSecret: secretRef.current,
        params: {
          payment_method_type: "us_bank_account",
          // The address is not asked for here: it goes to the server with the confirmation.
          payment_method_data: { billing_details: { name: who.name.trim(), ...(who.email.trim() ? { email: who.email.trim() } : {}) } },
        },
        expand: ["payment_method"],
      });
      if (error) setBankError(error.message ?? TRY_AGAIN);
      // Closed without choosing an account: nothing changes.
      else if (setupIntent?.status === "requires_confirmation") setBank(bankOf(setupIntent));
    } catch {
      setBankError(TRY_AGAIN);
    } finally {
      setConnecting(false);
    }
  };

  // The address fields, once they're wanted (no address known, or Edit) and the form is in.
  useEffect(() => {
    const elements = elementsRef.current;
    if (!editing || !elements || phase !== "ready" || !addrRef.current) return;
    const address = elements.create("address", {
      mode: "billing",
      allowedCountries: ["US"],
      defaultValues: { name: props.company ?? tenant.value?.name ?? "", address: known ? toStripe(known) : { country: "US" } },
      display: { name: "organization" },
    });
    address.on("change", (e) => {
      const done = e.complete && e.value?.address ? e.value.address : null;
      current.current = done;
      onAddress.current?.(done ? toAddress(done) : null);
    });
    address.mount(addrRef.current);
    return () => {
      address.destroy();
      current.current = known ? toStripe(known) : null;
      onAddress.current?.(null);
    };
  }, [editing, phase]);

  if (phase === "failed") {
    return (
      <div class="bill-form is-failed" role="alert">
        <p class="t-body">{failure ?? "The payment form didn't load."}</p>
        <Button variant="secondary" icon="undo" onClick={() => setAttempt((n) => n + 1)}>
          Try Again
        </Button>
      </div>
    );
  }
  return (
    <div class="bill-form" aria-busy={phase === "ready" ? undefined : "true"}>
      {phase === "starting" ? (
        <Skeleton class="bill-form-wait" lines={6} />
      ) : (
        <div class="bill-kinds" role="group" aria-label="Pay by">
          {KINDS.map((k) => (
            <button key={k.id} type="button" class="bill-kind" aria-pressed={kind === k.id ? "true" : "false"} onClick={() => setKind(k.id)}>
              <Icon name={k.icon} size="sm" />
              {k.label}
            </button>
          ))}
        </div>
      )}
      {phase === "ready" && kind === "us_bank_account" ? (
        <div class="bill-bank">
          {bank ? (
            <div class="bill-bank-on">
              <Icon name="bank" />
              <span class="bill-bank-name">
                {bank.name}
                {bank.last4 ? <span class="bill-bank-last4"> ending {bank.last4}</span> : null}
              </span>
              <LinkButton onClick={() => void connect()}>Change</LinkButton>
            </div>
          ) : (
            <div class="bill-bank-off">
              <p class="bill-bank-l">Pay from your business checking account.</p>
              <Button variant="secondary" icon="bank" busy={connecting} onClick={() => void connect()}>
                Connect Your Bank
              </Button>
            </div>
          )}
          {bankError ? (
            <p class="bill-err" role="alert">
              <Icon name="info" size="sm" />
              {bankError}
            </p>
          ) : null}
          {bank ? <p class="bill-mandate">{BANK_MANDATE}</p> : null}
        </div>
      ) : null}
      <div class={cx("bill-pay", kind !== "card" && "is-off")} ref={cardRef} hidden={kind !== "card"} />
      {phase === "ready" && kind === "card" && !cardReady ? <Skeleton class="bill-form-wait" lines={4} /> : null}
      {phase === "starting" ? null : editing ? (
        <>
          <h4 class="bill-sub">Billing address</h4>
          <div class="bill-addr" ref={addrRef} />
        </>
      ) : (
        <div class="bill-addr-line">
          <span class="bill-k">Billing address</span>
          <span class="bill-addr-text">{known ? addressLine(known) : ""}</span>
          <LinkButton onClick={() => setEditing(true)}>Edit</LinkButton>
        </div>
      )}
    </div>
  );
}
